import React, { useState } from 'react'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { useAuth } from '../../hooks/useAuth'

// Mock data for calendar
const mockEvents = [
  {
    id: 1,
    title: 'AI in Modern Business',
    date: '2024-08-05',
    time: '09:00',
    platform: 'LinkedIn',
    status: 'scheduled',
    type: 'post'
  },
  {
    id: 2,
    title: 'Digital Transformation Tips',
    date: '2024-08-06',
    time: '14:30',
    platform: 'Twitter',
    status: 'draft',
    type: 'post'
  },
  {
    id: 3,
    title: 'Tech Innovation Roundup',
    date: '2024-08-07',
    time: '11:00',
    platform: 'Facebook',
    status: 'scheduled',
    type: 'post'
  },
  {
    id: 4,
    title: 'Weekly Content Review',
    date: '2024-08-08',
    time: '16:00',
    platform: 'all',
    status: 'completed',
    type: 'meeting'
  },
  {
    id: 5,
    title: 'Future of Work Series',
    date: '2024-08-09',
    time: '10:00',
    platform: 'Instagram',
    status: 'pending',
    type: 'post'
  }
]

const mockCalendar = {
  currentMonth: 'August 2024',
  weeks: [
    [
      { date: '2024-07-29', day: 29, isCurrentMonth: false, events: [] },
      { date: '2024-07-30', day: 30, isCurrentMonth: false, events: [] },
      { date: '2024-07-31', day: 31, isCurrentMonth: false, events: [] },
      { date: '2024-08-01', day: 1, isCurrentMonth: true, events: [] },
      { date: '2024-08-02', day: 2, isCurrentMonth: true, events: [] },
      { date: '2024-08-03', day: 3, isCurrentMonth: true, events: [] },
      { date: '2024-08-04', day: 4, isCurrentMonth: true, events: [] }
    ],
    [
      { date: '2024-08-05', day: 5, isCurrentMonth: true, events: [mockEvents[0]] },
      { date: '2024-08-06', day: 6, isCurrentMonth: true, events: [mockEvents[1]] },
      { date: '2024-08-07', day: 7, isCurrentMonth: true, events: [mockEvents[2]] },
      { date: '2024-08-08', day: 8, isCurrentMonth: true, events: [mockEvents[3]] },
      { date: '2024-08-09', day: 9, isCurrentMonth: true, events: [mockEvents[4]] },
      { date: '2024-08-10', day: 10, isCurrentMonth: true, events: [] },
      { date: '2024-08-11', day: 11, isCurrentMonth: true, events: [] }
    ],
    [
      { date: '2024-08-12', day: 12, isCurrentMonth: true, events: [] },
      { date: '2024-08-13', day: 13, isCurrentMonth: true, events: [] },
      { date: '2024-08-14', day: 14, isCurrentMonth: true, events: [] },
      { date: '2024-08-15', day: 15, isCurrentMonth: true, events: [] },
      { date: '2024-08-16', day: 16, isCurrentMonth: true, events: [] },
      { date: '2024-08-17', day: 17, isCurrentMonth: true, events: [] },
      { date: '2024-08-18', day: 18, isCurrentMonth: true, events: [] }
    ],
    [
      { date: '2024-08-19', day: 19, isCurrentMonth: true, events: [] },
      { date: '2024-08-20', day: 20, isCurrentMonth: true, events: [] },
      { date: '2024-08-21', day: 21, isCurrentMonth: true, events: [] },
      { date: '2024-08-22', day: 22, isCurrentMonth: true, events: [] },
      { date: '2024-08-23', day: 23, isCurrentMonth: true, events: [] },
      { date: '2024-08-24', day: 24, isCurrentMonth: true, events: [] },
      { date: '2024-08-25', day: 25, isCurrentMonth: true, events: [] }
    ],
    [
      { date: '2024-08-26', day: 26, isCurrentMonth: true, events: [] },
      { date: '2024-08-27', day: 27, isCurrentMonth: true, events: [] },
      { date: '2024-08-28', day: 28, isCurrentMonth: true, events: [] },
      { date: '2024-08-29', day: 29, isCurrentMonth: true, events: [] },
      { date: '2024-08-30', day: 30, isCurrentMonth: true, events: [] },
      { date: '2024-08-31', day: 31, isCurrentMonth: true, events: [] },
      { date: '2024-09-01', day: 1, isCurrentMonth: false, events: [] }
    ]
  ]
}

const CalendarPage: React.FC = () => {
  const { user } = useAuth()
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [view, setView] = useState<'month' | 'week' | 'day'>('month')

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
      case 'draft': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
      case 'pending': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
      case 'completed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
    }
  }

  const getPlatformColor = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'linkedin': return 'bg-blue-500'
      case 'twitter': return 'bg-sky-500'
      case 'facebook': return 'bg-indigo-500'
      case 'instagram': return 'bg-pink-500'
      default: return 'bg-gray-500'
    }
  }

  const isToday = (date: string) => {
    const today = new Date().toISOString().split('T')[0]
    return date === today
  }

  return (
    <div className="space-y-8 fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold gradient-text">
            Content Calendar
          </h1>
          <p className="text-muted-foreground mt-2">
            Schedule and manage your content across all platforms
          </p>
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" size="sm" className="btn-outline">Today</Button>
          <Button variant="outline" size="sm" className="btn-outline">Previous</Button>
          <Button variant="outline" size="sm" className="btn-outline">Next</Button>
          <Button className="btn-primary">+ New Event</Button>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        <Button 
          variant={view === 'month' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setView('month')}
          className={view === 'month' ? 'btn-primary' : 'btn-outline'}
        >
          Month
        </Button>
        <Button 
          variant={view === 'week' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setView('week')}
          className={view === 'week' ? 'btn-primary' : 'btn-outline'}
        >
          Week
        </Button>
        <Button 
          variant={view === 'day' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setView('day')}
          className={view === 'day' ? 'btn-primary' : 'btn-outline'}
        >
          Day
        </Button>
      </div>

      {/* Calendar Grid */}
      <Card className="card-modern slide-up" style={{ animationDelay: '0.1s' }}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold">{mockCalendar.currentMonth}</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="btn-outline">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Button>
              <Button variant="outline" size="sm" className="btn-outline">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Button>
            </div>
          </div>

          {/* Calendar Header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {mockCalendar.weeks.flat().map((day, index) => (
              <div
                key={index}
                className={`min-h-[120px] p-2 border border-border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors ${
                  !day.isCurrentMonth ? 'text-muted-foreground/50' : ''
                } ${isToday(day.date) ? 'ring-2 ring-primary' : ''}`}
                onClick={() => setSelectedDate(day.date)}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-medium ${
                    isToday(day.date) ? 'text-primary font-bold' : ''
                  }`}>
                    {day.day}
                  </span>
                  {day.events.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {day.events.length}
                    </Badge>
                  )}
                </div>
                
                <div className="space-y-1">
                  {day.events.slice(0, 2).map((event) => (
                    <div
                      key={event.id}
                      className="text-xs p-1 rounded bg-muted/50 truncate"
                      title={event.title}
                    >
                      <div className="flex items-center gap-1">
                        <div className={`w-2 h-2 rounded-full ${getPlatformColor(event.platform)}`} />
                        <span className="truncate">{event.title}</span>
                      </div>
                    </div>
                  ))}
                  {day.events.length > 2 && (
                    <div className="text-xs text-muted-foreground">
                      +{day.events.length - 2} more
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Upcoming Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="card-modern slide-up" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold">Upcoming Events</h3>
            <Button variant="outline" size="sm" className="btn-outline">View All</Button>
          </div>
          
          <div className="space-y-4">
            {mockEvents.slice(0, 5).map((event) => (
              <div key={event.id} className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${getPlatformColor(event.platform)}`} />
                    <div>
                      <h4 className="font-medium text-sm">{event.title}</h4>
                      <p className="text-xs text-muted-foreground">
                        {new Date(event.date).toLocaleDateString()} at {event.time}
                      </p>
                    </div>
                  </div>
                  <Badge className={`text-xs ${getStatusColor(event.status)}`}>
                    {event.status}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="capitalize">{event.platform}</span>
                  <span>•</span>
                  <span className="capitalize">{event.type}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="card-modern slide-up" style={{ animationDelay: '0.3s' }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold">Quick Actions</h3>
          </div>
          
          <div className="space-y-3">
            <Button className="w-full justify-start btn-outline">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Schedule New Post
            </Button>
            
            <Button className="w-full justify-start btn-outline">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Create Content Series
            </Button>
            
            <Button className="w-full justify-start btn-outline">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Import from Library
            </Button>
            
            <Button className="w-full justify-start btn-outline">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Bulk Schedule
            </Button>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default CalendarPage